// Exclusive Portal - Route /shopee atau /tiktok - Mobile Optimized
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CuratedProduct, Platform, Category } from '../types';
import { getDummyProducts } from '../data/dummyProducts';
import ProductCard from '../components/ProductCard';
import CategoryTabs from '../components/CategoryTabs';

const CATEGORIES = [
  { value: 'skincare' as Category, label: 'Skincare', icon: '🧴' },
  { value: 'fashion' as Category, label: 'Fashion', icon: '👗' },
  { value: 'elektronik' as Category, label: 'Elektronik', icon: '📱' },
  { value: 'furniture' as Category, label: 'Furniture', icon: '🏠' },
  { value: 'olahraga' as Category, label: 'Olahraga', icon: '⚽' },
];

export default function ExclusivePortal() {
  const params = useParams<{ platform: string }>();
  const urlPlatform = params.platform || '';
  
  const isValidPlatform = (p: string): p is Platform => {
    return p === 'shopee' || p === 'tiktok';
  };
  
  const currentPlatform: Platform = isValidPlatform(urlPlatform) ? urlPlatform : 'shopee';
  
  const [products, setProducts] = useState<CuratedProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<CuratedProduct[]>([]);
  const [category, setCategory] = useState<Category>('skincare');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [usingDummyData, setUsingDummyData] = useState(false);

  const platformConfig = {
    shopee: { name: 'Shopee', gradient: 'from-orange-500 to-red-500', bgGradient: 'from-orange-500/10 to-red-500/5', border: 'border-orange-500', text: 'text-orange-400', bg: 'bg-orange-500', emoji: '🛒' },
    tiktok: { name: 'TikTok', gradient: 'from-pink-500 to-purple-500', bgGradient: 'from-pink-500/10 to-purple-500/5', border: 'border-pink-500', text: 'text-pink-400', bg: 'bg-pink-500', emoji: '🎵' },
  };

  const config = platformConfig[currentPlatform];

  useEffect(() => { loadProducts(); }, [category, currentPlatform]);
  useEffect(() => { filterProducts(); }, [products, searchQuery]);

  const loadProducts = async () => {
    setLoading(true);
    setUsingDummyData(false);
    
    try {
      const apiUrl = `https://affiliate-tracker.modernshopp.workers.dev/api/holy-grail?platform=${currentPlatform}&category=${category}`;
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'success' && data.products && data.products.length > 0) {
          setProducts(data.products);
          setUsingDummyData(false);
        } else {
          console.log(`[${currentPlatform.toUpperCase()}] API returned empty, using dummy data`);
          const dummy = getDummyProducts(currentPlatform, category);
          setProducts(dummy);
          setUsingDummyData(true);
        }
      } else {
        console.log(`[${currentPlatform.toUpperCase()}] API error, using dummy data`);
        const dummy = getDummyProducts(currentPlatform, category);
        setProducts(dummy);
        setUsingDummyData(true);
      }
    } catch (error) {
      console.error(`[${currentPlatform.toUpperCase()}] Network error, using dummy data`);
      const dummy = getDummyProducts(currentPlatform, category);
      setProducts(dummy);
      setUsingDummyData(true);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    const queryTrimmed = searchQuery.trim();
    if (queryTrimmed.length === 0) {
      setFilteredProducts(products);
      return;
    }
    const query = queryTrimmed.toLowerCase();
    setFilteredProducts(products.filter(p => p.title.toLowerCase().includes(query)));
  };

  const copyAffiliateLink = (link: string) => {
    navigator.clipboard.writeText(link);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header - Compact on mobile */}
      <header className={`bg-gradient-to-r ${config.bgGradient} border-b ${config.border} px-3 md:px-6 py-2 md:py-4 sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/" className="text-slate-300 hover:text-white transition-colors font-medium text-sm md:text-base">
              ←
            </Link>
            <div className={`text-xl md:text-3xl font-bold ${config.text}`}>
              {config.emoji} <span className="hidden sm:inline">{config.name} Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {usingDummyData && (
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-[10px] md:text-xs font-medium border border-amber-500/30">
                📦 Demo
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Controls - Mobile optimized */}
      <div className="p-3 md:p-6 max-w-7xl mx-auto space-y-3 md:space-y-4">
        {/* Category Tabs - Using CategoryTabs component */}
        <CategoryTabs
          categories={CATEGORIES}
          selected={category}
          onSelect={setCategory}
          platformConfig={config}
        />

        {/* Search Bar - Compact on mobile */}
        <input
          type="text"
          placeholder={`🔍 Cari produk...`}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 md:px-4 md:py-3 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 text-sm md:text-base focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
        />

        {/* Results Count */}
        <div className="flex items-center gap-2 text-slate-400 text-xs md:text-sm">
          <span>📊 {filteredProducts.length}/{products.length} produk</span>
          {usingDummyData && (
            <span className="text-amber-400 text-[10px] md:text-xs bg-amber-500/10 px-2 py-0.5 rounded-full">
              Data demo
            </span>
          )}
        </div>
      </div>

      {/* Product Grid - 2 columns on mobile, 4 on lg */}
      <div className="px-3 md:px-6 pb-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 md:h-64 gap-3">
            <div className="w-8 h-8 md:w-12 md:h-12 border-3 md:border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="text-amber-400 text-sm md:text-xl animate-pulse">
              ⏳ Memuat...
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-500">
            <div className="text-5xl md:text-7xl mb-3 md:mb-4">📦</div>
            <p className="text-base md:text-xl font-medium mb-2">Tidak ada produk</p>
            <p className="text-xs md:text-sm">Pipeline funnel jam 02.00 WIB</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {filteredProducts.map(product => (
              <ProductCard 
                key={product.id} 
                product={product} 
                platformConfig={config} 
                onCopyLink={copyAffiliateLink} 
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer - Compact */}
      <footer className="bg-slate-800 border-t border-slate-700 px-3 md:px-6 py-2 md:py-3 text-center text-slate-500 text-[10px] md:text-sm">
        🏆 Holy Grail v2.2 • Pipeline: 02.00 WIB • 300 Produk
      </footer>
    </div>
  );
}
